import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Calendar, ArrowLeft, Clock, Trash2, Eye, EyeOff, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = "https://www.achievesalesalliance.net";

const scrollToTop = () => window.scrollTo(0, 0);

const AUTOLINK_CITIES = [
  "Houston", "Dallas", "San Antonio", "Austin", "Fort Worth", "El Paso", "Arlington", "Corpus Christi",
  "Plano", "Laredo", "Lubbock", "Irving", "Garland", "Frisco", "McKinney", "Amarillo", "Grand Prairie",
  "Brownsville", "Killeen", "Pasadena", "McAllen", "Mesquite", "Midland", "Denton", "Waco", "Carrollton",
  "Round Rock", "Abilene", "Pearland", "Richardson", "Odessa", "Beaumont", "College Station", "Tyler",
  "League City", "Allen", "Sugar Land", "Edinburg", "Conroe", "San Marcos", "New Braunfels", "Temple",
  "Flower Mound", "Cedar Park", "Pflugerville", "Georgetown", "Mansfield", "Victoria", "Wylie", "Rockwall"
];
const AUTOLINK_INDUSTRIES = [
  { term: "roofing sales recruiting", to: "/roofing-sales-recruiting/houston" },
  { term: "solar sales recruiting", to: "/solar-sales-recruiting/houston" },
  { term: "insurance sales recruiting", to: "/insurance-sales-recruiting/houston" },
  { term: "roofing sales jobs", to: "/roofing-sales-jobs/houston" },
  { term: "solar sales jobs", to: "/solar-sales-jobs/houston" },
  { term: "insurance sales jobs", to: "/insurance-sales-jobs/houston" },
  { term: "roofing sales", to: "/roofing-sales-recruiting/houston" },
  { term: "solar sales", to: "/solar-sales-recruiting/houston" },
  { term: "insurance sales", to: "/insurance-sales-recruiting/houston" },
];

const autoLinkContent = (html) => {
  let result = html;
  AUTOLINK_CITIES.forEach(city => {
    const slug = city.toLowerCase().replace(/\s+/g, '-');
    const regex = new RegExp(`(?<!<a[^>]*>)(?<!href=")\\b(${city})\\b(?![^<]*<\\/a>)`, 'g');
    let replaced = false;
    result = result.replace(regex, (match) => {
      if (replaced) return match;
      replaced = true;
      return `<a href="/sales-recruiting/${slug}" class="text-amber-500 hover:text-amber-400 underline">${match}</a>`;
    });
  });
  AUTOLINK_INDUSTRIES.forEach(({ term, to }) => {
    const regex = new RegExp(`(?<!<a[^>]*>)(?<!href=")(${term})(?![^<]*<\\/a>)`, 'gi');
    let replaced = false;
    result = result.replace(regex, (match) => {
      if (replaced) return match;
      replaced = true;
      return `<a href="${to}" class="text-amber-500 hover:text-amber-400 underline">${match}</a>`;
    });
  });
  return result;
};

const renderMarkdown = (text) => {
  if (!text) return '';
  let html = text
    .replace(/^### (.*?)$/gm, '<h3 class="text-xl font-bold text-white mt-8 mb-3">$1</h3>')
    .replace(/^## (.*?)$/gm, '<h2 class="text-2xl font-bold text-white mt-10 mb-4">$1</h2>')
    .replace(/^# (.*?)$/gm, '<h2 class="text-2xl font-bold text-white mt-6 mb-4">$1</h2>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" class="text-amber-500 hover:text-amber-400 underline" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^\d+\.\s+(.*?)$/gm, '<li class="ml-4 mb-1">$1</li>')
    .replace(/^- (.*?)$/gm, '<li class="ml-4 mb-1">$1</li>')
    .replace(/(<li.*<\/li>\n?)+/g, '<ul class="list-disc pl-4 mb-4 space-y-1">$&</ul>')
    .replace(/\n\n/g, '</p><p class="text-gray-300 leading-relaxed mb-4">')
    .replace(/\n/g, '<br/>');
  return autoLinkContent(`<p class="text-gray-300 leading-relaxed mb-4">${html}</p>`);
};

// ==================== BLOG LIST ====================
export const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Sales Recruiting Blog | Achieve Sales Alliance";
    axios.get(`${API}/blog/posts?published_only=true`)
      .then(r => { setPosts(r.data.posts); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <nav className="bg-black border-b border-amber-500/20 px-4 py-3">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Link to="/" onClick={scrollToTop}><img src="/asa-logo-horizontal.png" alt="ASA" className="h-10" /></Link>
          <Link to="/" onClick={scrollToTop} className="text-amber-500 text-sm">Home</Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Sales Recruiting <span className="text-amber-500">Insights</span></h1>
          <p className="text-gray-400">Expert tips on building high-performing sales teams in Texas.</p>
        </motion.div>

        {loading ? (
          <div className="text-center py-12"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg mb-4">Coming Soon</p>
            <p className="text-gray-500">Expert articles on sales recruiting are on the way.</p>
            <Link to="/" onClick={scrollToTop} className="inline-block mt-6 text-amber-500 hover:text-amber-400 font-medium">
              &larr; Back to Home
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post, i) => (
              <motion.div key={post.id || i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={`/blog/${post.slug}`} onClick={scrollToTop}
                  className="block bg-gray-900/50 border border-gray-800 hover:border-amber-500/50 rounded-xl p-6 transition-all group"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <h2 className="text-xl font-bold group-hover:text-amber-500 transition-colors mb-2">{post.title}</h2>
                  <p className="text-gray-400 text-sm mb-3 line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(post.created_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {Math.ceil((post.content?.length || 0) / 1000)} min read</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {posts.length > 0 && (
          <div className="mt-12 text-center">
            <Link to="/" onClick={scrollToTop} className="text-gray-400 hover:text-amber-500 font-medium transition-colors">
              &larr; Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== SINGLE BLOG POST ====================
export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/blog/posts/${slug}`)
      .then(r => {
        setPost(r.data);
        document.title = `${r.data.title} | Achieve Sales Alliance`;
        // Article Schema for Google AI Mode
        const articleSchema = { "@context": "https://schema.org", "@type": "Article", "headline": r.data.title, "author": { "@type": "Organization", "name": "Achieve Sales Alliance", "url": "https://www.achievesalesalliance.net" }, "publisher": { "@type": "Organization", "name": "Achieve Sales Alliance", "url": "https://www.achievesalesalliance.net" }, "datePublished": r.data.created_at, "dateModified": r.data.created_at, "mainEntityOfPage": { "@type": "WebPage", "@id": `https://www.achievesalesalliance.net/blog/${r.data.slug}` }, "description": r.data.excerpt || r.data.title, "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["[data-testid='post-title']", "[data-testid='post-content'] p:first-of-type", "[data-testid='post-content'] h2"] } };
        let sc = document.querySelector('#article-schema'); if (!sc) { sc = document.createElement('script'); sc.id = 'article-schema'; sc.type = 'application/ld+json'; document.head.appendChild(sc); }
        sc.textContent = JSON.stringify(articleSchema);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-gray-950 flex items-center justify-center"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!post) return <div className="min-h-screen bg-gray-950 flex items-center justify-center text-white">Post not found.</div>;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <nav className="bg-black border-b border-amber-500/20 px-4 py-3">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Link to="/" onClick={scrollToTop}><img src="/asa-logo-horizontal.png" alt="ASA" className="h-10" /></Link>
          <Link to="/blog" onClick={scrollToTop} className="text-amber-500 text-sm">&larr; All Articles</Link>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(post.created_at).toLocaleDateString()}</span>
          <span className="flex items-center gap-1"><Clock size={14} /> {Math.ceil((post.content?.length || 0) / 1000)} min read</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-8" data-testid="post-title">{post.title}</h1>
        <div className="prose prose-invert max-w-none" data-testid="post-content"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }} />

        {/* INTERNAL LINKS TO MONEY PAGES */}
        <div className="mt-10 p-6 bg-gray-900 border border-gray-800 rounded-xl" data-testid="blog-related-pages">
          <h3 className="text-lg font-bold text-amber-500 mb-4">Explore Sales Recruiting by Industry</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { label: "Roofing Sales Recruiting in Texas", to: "/sales-recruiting/houston" },
              { label: "Solar Sales Recruiting in Texas", to: "/solar-sales-recruiting/dallas" },
              { label: "Insurance Sales Recruiting in Texas", to: "/insurance-sales-recruiting/san-antonio" },
              { label: "Roofing Sales Jobs in Texas", to: "/roofing-sales-jobs/austin" },
              { label: "Solar Sales Jobs in Texas", to: "/solar-sales-jobs/fort-worth" },
              { label: "Insurance Sales Jobs in Texas", to: "/insurance-sales-jobs/houston" },
            ].map((link, i) => (
              <Link key={i} to={link.to} onClick={scrollToTop} className="text-gray-400 hover:text-amber-500 text-sm py-1 transition-colors">
                &rarr; {link.label}
              </Link>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-800">
            <h4 className="text-sm font-bold text-gray-500 mb-2">Top Texas Cities</h4>
            <div className="flex flex-wrap gap-2">
              {["Houston", "Dallas", "San Antonio", "Austin", "Fort Worth", "El Paso", "Plano", "Frisco"].map((city, i) => (
                <Link key={i} to={`/sales-recruiting/${city.toLowerCase().replace(/\s+/g, '-')}`} onClick={scrollToTop} className="text-xs text-gray-500 hover:text-amber-500 bg-gray-800 px-2 py-1 rounded transition-colors">
                  {city}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 p-6 bg-gradient-to-r from-amber-600 to-amber-500 rounded-xl text-center">
          <h3 className="text-xl font-bold text-black mb-2">Ready to Build Your Sales Team?</h3>
          <p className="text-black/80 mb-4">Get a free recruiting consultation from Achieve Sales Alliance.</p>
          <a
            href="https://www.achievesalesalliance.net/#quote"
            onClick={(e) => { e.preventDefault(); window.location.href = '/#quote'; setTimeout(() => { const el = document.getElementById('quote'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 300); }}
            className="inline-block bg-black hover:bg-gray-900 text-amber-500 px-8 py-3 rounded-lg font-semibold transition-all cursor-pointer"
            data-testid="blog-cta-btn"
          >
            Get Your Free Consultation
          </a>
        </div>

        <div className="mt-8 text-center">
          <Link to="/blog" onClick={scrollToTop} className="text-gray-400 hover:text-amber-500 font-medium transition-colors">
            &larr; More Articles
          </Link>
        </div>
      </article>
    </div>
  );
};

// ==================== ADMIN BLOG MANAGER ====================
export const AdminBlogManager = ({ token, api }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const loadPosts = async () => {
    try {
      const res = await axios.get(`${api}/admin/blog/posts`, { headers });
      setPosts(res.data.posts);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadPosts(); // eslint-disable-next-line
  }, []);

  const generatePost = async () => {
    setGenerating(true);
    try {
      await axios.post(`${api}/admin/blog/generate`, {}, { headers });
      await loadPosts();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to generate'); }
    setGenerating(false);
  };

  const togglePublish = async (post) => {
    await axios.patch(`${api}/admin/blog/posts/${post.id}`, { published: !post.published }, { headers });
    await loadPosts();
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    await axios.delete(`${api}/admin/blog/posts/${post.id}`, { headers });
    await loadPosts();
  };

  const startEdit = (post) => { setEditingPost(post); setEditTitle(post.title); setEditContent(post.content); };

  const saveEdit = async () => {
    await axios.patch(`${api}/admin/blog/posts/${editingPost.id}`, { title: editTitle, content: editContent }, { headers });
    setEditingPost(null);
    await loadPosts();
  };

  if (editingPost) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-bold mb-4">Edit Post</h3>
        <input value={editTitle} onChange={e => setEditTitle(e.target.value)}
          className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 mb-3 focus:border-amber-500 focus:outline-none" />
        <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={20}
          className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 mb-3 font-mono text-sm focus:border-amber-500 focus:outline-none" />
        <div className="flex gap-3">
          <button onClick={saveEdit} className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-medium">Save</button>
          <button onClick={() => setEditingPost(null)} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold">Blog Posts ({posts.length})</h3>
        <button onClick={generatePost} disabled={generating} data-testid="generate-blog-btn"
          className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-black px-4 py-2 rounded-lg font-medium flex items-center gap-2">
          <Sparkles size={16} /> {generating ? 'Generating...' : 'AI Generate Article'}
        </button>
      </div>
      <div className="space-y-3">
        {posts.map(post => (
          <div key={post.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex justify-between items-center">
            <div className="flex-1">
              <h4 className="font-medium text-white">{post.title}</h4>
              <p className="text-gray-500 text-xs mt-1">
                {new Date(post.created_at).toLocaleDateString()} ·
                <span className={post.published ? 'text-green-400' : 'text-yellow-400'}> {post.published ? 'Published' : 'Draft'}</span>
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(post)} className="text-gray-400 hover:text-amber-500 p-1" title="Edit">
                <FileText size={16} />
              </button>
              <button onClick={() => togglePublish(post)} className="text-gray-400 hover:text-green-400 p-1" title={post.published ? 'Unpublish' : 'Publish'}>
                {post.published ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button onClick={() => deletePost(post)} className="text-gray-400 hover:text-red-400 p-1" title="Delete">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {posts.length === 0 && (
          <p className="text-center text-gray-500 py-8">No blog posts yet — Click 'AI Generate Article' to create your first post!</p>
        )}
      </div>
    </div>
  );
};

const FileText = ({ size = 24, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
  </svg>
);
